import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Radio, Switch } from 'antd';
import { User } from '../../interfaces/userManagement';
import orgManagementService from '../../apiServices/orgManagementService';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { getRoleLabel, ROLE_OPTIONS, USER_ROLES } from '../../utils/roles';

interface UserFormModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    initialValues?: User | null;
}

const UserEditFormModal: React.FC<UserFormModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    initialValues,
}) => {
    // console.log(initialValues);

    const [form] = Form.useForm();
    const { currentOrg, availableOrgs } = useAuthOrg();
    const [loading, setLoading] = useState(false);
    const [userMode, setUserMode] = useState<'self-served' | 'managed'>(initialValues?.userMode || 'self-served');

    useEffect(() => {
        if (visible && initialValues) {
            // Set userMode from initialValues or infer from accessibleOrgs length
            const mode = initialValues.userMode ||
                (initialValues.accessibleOrgs && initialValues.accessibleOrgs.length > 1 ? 'managed' : 'self-served');
            setUserMode(mode);

            form.setFieldsValue({
                ...initialValues,
                email: initialValues.userEmail,
                role: initialValues.userRole,
                org_ids: initialValues.accessibleOrgs?.map(org => org.orgId)
            });
        } else {
            form.resetFields();
            setUserMode('self-served');
        }
    }, [visible, initialValues, form]);

    const handleOk = async () => {
        if (!currentOrg) return;

        try {
            const values = await form.validateFields();
            setLoading(true);

            if (initialValues) {
                // Update user access - enforce single org for self-served users
                const orgIds = values.org_ids || [currentOrg.orgId];

                // Validate: self-served users can only have one org
                if (userMode === 'self-served' && orgIds.length > 1) {
                    message.error('Self-served users can only have access to one account');
                    return;
                }

                await orgManagementService.updateUserAccess({
                    userId: parseInt(initialValues.id),
                    orgIds: orgIds,
                    viewPiiAccess: Boolean(values.viewPiiAccess),
                });
                message.success('User access updated successfully');
            }
            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error('Error saving user:', error);
            message.error('Failed to save user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={initialValues ? "Edit User Access" : "Add User"}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={initialValues ? "Update Access" : "Create User"}
            okButtonProps={{ className: 'bg-[#1F344C]' }}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ role: USER_ROLES.VIEWER, org_ids: [currentOrg?.orgId], userMode: 'self-served' }}
            >
                {!initialValues && (
                    <>
                        <Form.Item
                            name="userMode"
                            label="User Mode"
                            initialValue="self-served"
                        >
                            <Radio.Group onChange={(e) => {
                                setUserMode(e.target.value);
                                form.setFieldsValue({ org_ids: [] }); // Reset org selection on mode change
                            }}>
                                <Radio value="self-served">Self-Served</Radio>
                                <Radio value="managed">Managed</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="userName"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter the user name' }]}
                        >
                            <Input placeholder="John Doe" />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[
                                { required: true, message: 'Please enter the email address' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}
                        >
                            <Input placeholder="john@example.com" />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[{ required: true, message: 'Please enter the password' }]}
                        >
                            <Input.Password placeholder="Enter password" />
                        </Form.Item>

                        <Form.Item
                            name="role"
                            label="Role"
                            rules={[{ required: true, message: 'Please select a role' }]}
                        >
                            <Select>
                                {ROLE_OPTIONS.map((role) => (
                                    <Select.Option key={role.value} value={role.value}>
                                        {role.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="mobile"
                            label="Mobile Number (Optional)"
                        >
                            <Input placeholder="+1234567890" />
                        </Form.Item>
                    </>
                )}

                {initialValues && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9'
                    }}>
                        <div style={{ fontSize: '14px', color: '#595959', marginBottom: '4px' }}>
                            User Mode
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {userMode === 'self-served' ? 'Self-Served' : 'Managed'}
                            <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: '8px' }}>
                                ({userMode === 'self-served' ? 'Can access only 1 account' : 'Can access multiple accounts'})
                            </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#595959', marginTop: '12px', marginBottom: '4px' }}>
                            Role
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {getRoleLabel(initialValues.userRole)}
                        </div>
                    </div>
                )}

                <Form.Item
                    name="org_ids"
                    label="Accessible Accounts"
                    rules={[{ required: true, message: 'Please select at least one account' }]}
                >
                    <Select
                        mode="multiple"
                        placeholder={userMode === 'self-served' ? "Select account" : "Select accounts"}
                        style={{ width: '100%' }}
                        maxCount={userMode === 'self-served' ? 1 : undefined}
                    >
                        {availableOrgs.map(org => (
                            <Select.Option key={org.orgId} value={org.orgId}>
                                {org.orgName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="viewPiiAccess"
                    label="PII access"
                    valuePropName="checked"
                    initialValue={Boolean(initialValues?.viewPiiAccess)}
                >
                    <Switch checkedChildren="Allowed" unCheckedChildren="Not allowed" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UserEditFormModal;
